const PREVIEW_LIMIT = 15;

const hasCompleteAthleteSelectionPreview = (
  preview,
  expectedCount = PREVIEW_LIMIT
) => {
  const requiredCount = Math.max(1, Number(expectedCount) || PREVIEW_LIMIT);
  return Array.isArray(preview) && preview.length >= requiredCount;
};

const normalizeProjectedStatKey = (statKey) => {
  if (typeof statKey !== "string") {
    return statKey;
  }

  return statKey
    .replace(/^goblin_/, "projected_")
    .replace(/^demon_/, "projected_")
    .replace(
      /^(projected_|promo_)?(?:three_pointers_made|three_points_made|threes_made|made_threes)$/,
      (_, prefix = "") => `${prefix}3ptm`
    )
    .replace(/plus\/minus$/, "plus_minus");
};

const getLeagueKey = (athlete) => String(
  athlete?._top_play_source_league || athlete?.league || ""
).trim().toLowerCase();

const filterAthletesForRequestedStat = (
  candidates,
  selectedStat,
  popularStat = "projected_popular"
) => {
  const rows = Array.isArray(candidates) ? candidates : [];
  const statKey = normalizeProjectedStatKey(selectedStat || "");

  if (!statKey || statKey === normalizeProjectedStatKey(popularStat)) {
    return rows;
  }

  return rows.filter((athlete) => (
    normalizeProjectedStatKey(athlete?.stat_key || "") === statKey
  ));
};

const getStandardAltStatKey = (athlete, selectedStat) => {
  const altStats = athlete?.alt_stats && typeof athlete.alt_stats === "object"
    ? athlete.alt_stats
    : {};
  const normalizedStat = normalizeProjectedStatKey(selectedStat || "");

  if (Object.prototype.hasOwnProperty.call(altStats, normalizedStat)) {
    return normalizedStat;
  }

  return Object.keys(altStats).find((statKey) => (
    statKey.startsWith("projected_") && normalizeProjectedStatKey(statKey) === normalizedStat
  )) || "";
};

const buildAthleteSelectionPreview = (
  candidates,
  selectedSport,
  selectedStat,
  statsMap = {},
  limit = PREVIEW_LIMIT
) => {
  const sportKey = String(selectedSport || "").trim().toLowerCase();
  const statKey = normalizeProjectedStatKey(selectedStat || "");
  const isPopular = statKey === "projected_popular";
  const seen = new Set();
  const preview = [];

  for (const athlete of Array.isArray(candidates) ? candidates : []) {
    if (!athlete || getLeagueKey(athlete) !== sportKey) {
      continue;
    }

    let previewAthlete = athlete;
    if (!isPopular) {
      const altStatKey = getStandardAltStatKey(athlete, statKey);
      if (!altStatKey) {
        continue;
      }

      const statValue = athlete.alt_stats[altStatKey];
      if (statValue === null || statValue === undefined || String(statValue).trim() === "") {
        continue;
      }

      previewAthlete = {
        ...athlete,
        current_stat: null,
        easy: false,
        hard: false,
        og_value: false,
        pp_stat: false,
        promo: false,
        stat: statValue,
        stat_display: statsMap?.[selectedStat] || statsMap?.[statKey] || athlete.stat_display,
        stat_id: "",
        stat_key: statKey,
      };
    }

    // Provider athlete IDs may be market-specific. Prefer stable taxonomy or
    // person/game identity so several cached markets for one athlete cannot
    // occupy multiple preview slots (the Azurá Stevens failure mode).
    const stablePersonIdentity =
      previewAthlete.term_id ||
      previewAthlete.uid ||
      [
        previewAthlete.game_id || "",
        String(previewAthlete.team_short || previewAthlete.team || "").trim().toLowerCase(),
        String(previewAthlete.name || "").trim().toLowerCase(),
      ].join("|");
    const identity = String(stablePersonIdentity || previewAthlete.athlete_id || "");
    const previewKey = `${identity}|${statKey || previewAthlete.stat_key || ""}`;
    if (seen.has(previewKey)) {
      continue;
    }

    seen.add(previewKey);
    preview.push({ ...previewAthlete, _boardOptimisticPreview: true });
    if (preview.length >= Math.max(1, Number(limit) || PREVIEW_LIMIT)) {
      break;
    }
  }

  return preview;
};

module.exports = {
  buildAthleteSelectionPreview,
  filterAthletesForRequestedStat,
  getStandardAltStatKey,
  hasCompleteAthleteSelectionPreview,
};
